<script setup>
import { useDisplay } from "vuetify"
import { parsePhoneNumber } from 'libphonenumber-js'
import { computed, ref, watch, onMounted, useAttrs } from "vue"
import { countries } from "@/utils/countries"


// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  popularCountries: {
    type: Array,
    default: () => ['PL', 'DE', 'GB', 'US', 'FR', 'IT', 'ES', 'UA', 'CZ'],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  prefixDisabled: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  prefixLabel: {
    type: String,
  },
  phoneNumberLabel: {
    type: String,
  },
})

// Emits
const emit = defineEmits(['update:modelValue'])

// All not know attributes
const attrs = useAttrs()

const countryPrefixes = computed(() => {
  // Popular countries in preferred order - can be passed via props
  const popularCountries = props.popularCountries

  const mappedCountries = countries.map(country => ({
    countryCode: country.iso,
    prefix: country.code.startsWith('+') ? country.code : `+${country.code}`,
    country: country.country,
  }))

  // Split into popular and remaining countries
  const popular = []
  const remaining = []

  mappedCountries.forEach(country => {
    if (popularCountries.includes(country.countryCode)) {
      popular.push(country)
    } else {
      remaining.push(country)
    }
  })

  // Sort popular countries by defined order
  popular.sort((a, b) => {
    const indexA = popularCountries.indexOf(a.countryCode)
    const indexB = popularCountries.indexOf(b.countryCode)

    return indexA - indexB
  })

  // Sort remaining countries alphabetically
  remaining.sort((a, b) => a.country.localeCompare(b.country))

  // Combine: popular first, then remaining
  return [...popular, ...remaining]
})

const phoneNumber = computed({
  get() {
    try {
      return parsePhoneNumber(localPhoneNumber.value, prefix.value)
    } catch (error) {
      return null
    }
  },
  set(value) {
    if(value === null || value === '') {
      prefix.value = 'PL'
      localPhoneNumber.value = ''
      isInternalUpdate.value = true
      emit('update:modelValue', '')

      return
    }

    let parsed = null
    try {
      parsed = parsePhoneNumber(value)
    } catch (err) {}

    if (parsed) {
      prefix.value = parsed.country ?? countryPrefixes.value.find(item => item.prefix === '+'+parsed.countryCallingCode)?.countryCode
      localPhoneNumber.value = parsed.nationalNumber
    } else {
      localPhoneNumber.value = value
    }
  },
})

const prefix = ref('PL')
const localPhoneNumber = ref('')
const { mobile } = useDisplay()

// Flag to prevent recursive updates
// eg. when u type anything in localPhoneNumber -> ctrl + a -> type any number once = this character won't be shown without this modification
const isInternalUpdate = ref(false)

// Initialize with modelValue
onMounted(() => {
  if (props.modelValue) {
    phoneNumber.value = props.modelValue
  }
})

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
  // Skip if this is an internal update to prevent recursion
  if (isInternalUpdate.value) {
    isInternalUpdate.value = false

    return
  }

  //console.log('External modelValue change:', newValue, 'current:', phoneNumber.value?.number)
  if (newValue !== phoneNumber.value?.number) {
    phoneNumber.value = newValue
  }
})

// Watch for prefix changes to emit updated phone number
watch([prefix, localPhoneNumber], async () => {
  //console.log('watch', prefix.value, localPhoneNumber.value)
  isInternalUpdate.value = true // Set flag before emitting to prevent watcher recursion
  emit('update:modelValue', phoneNumber.value?.number || '')
  await nextTick()
  isInternalUpdate.value = false
})

// Emit phoneNumber update
const updatePhoneNumber = (value) => {
  phoneNumber.value = value
  // Set flag before emitting to prevent watcher recursion
  isInternalUpdate.value = true
  emit('update:modelValue', phoneNumber.value?.number || '')
}

// Prevent typing anything other then +0..9
const protectPhoneInput = (event) => {
  const input = event.target
  let value = input.value

  // Allow only digits and + at the beginning
  value = value.replace(/[^\d+]/g, '') // Remove all non-digit and non-+ characters

  // Ensure + can only be at the beginning
  if (value.includes('+')) {
    const firstPlusIndex = value.indexOf('+')
    if (firstPlusIndex > 0) {
      // Remove + if it's not at the beginning
      value = value.replace(/\+/g, '')
    } else {
      // Keep only the first + and remove others
      value = '+' + value.substring(1).replace(/\+/g, '')
    }
  }

  // Update the input value if it was modified
  if (input.value !== value) {
    input.value = value
    localPhoneNumber.value = value
  }
}

// Reset prefix when user starts to search for new prefix
const resetPrefixToDefault = () => {
  prefix.value = null
}

// Reset prefix on blur when it's empty
const checkPrefix = () => {
  //console.log('checkPrefix', prefix.value)
  if(prefix.value === null) prefix.value = 'PL'
}
</script>

<template>
  <div class="phone-input-container">
    <VAutocomplete
      v-model="prefix"
      :items="countryPrefixes"
      :item-title="item => mobile ? `${item.prefix} (${item.countryCode})` : `${item.countryCode} (${item.prefix})`"
      :item-value="item => item.countryCode"
      :label="prefixLabel || $t('prefix')"
      :disabled="prefixDisabled || disabled"
      density="compact"
      variant="outlined"
      class="phone-prefix"
      color="secondary"
      hide-details
      auto-select-first
      chips
      :multiple="false"
      @input="resetPrefixToDefault"
      @blur="checkPrefix"
      @click="(pointerEvent) => pointerEvent.target.closest('.v-field__input')?.querySelector('input')?.focus()"
    />
    <VTextField
      v-model="localPhoneNumber"
      :label="phoneNumberLabel || $t('phone_no')"
      :autocomplete="generateUniqueId()"
      :disabled="disabled"
      density="compact"
      variant="outlined"
      class="phone-number force-text-align-left"
      color="secondary"
      hide-details
      type="tel"
      maxlength="15"
      control-variant="hidden"
      v-bind="attrs"
      @input="protectPhoneInput"
      @update:model-value="updatePhoneNumber"
    >
      <template #append-inner>
        <VProgressCircular
          v-if="props.isLoading"
          size="x-small"
          indeterminate
        />
      </template>
    </VTextField>
  </div>
</template>

<style>
.phone-input-container .v-field__input {
  display: flex!important;
  flex-wrap: nowrap!important;
}
</style>

<style scoped>
.phone-input-container {
  position: relative;
  display: flex;
}

.phone-prefix {
  flex: 0 1 155px;
  max-inline-size: 155px;
  min-inline-size: 110px;
}

.phone-number {
  flex: 1;
  min-inline-size: 150px;
}

/* stylelint-disable selector-pseudo-element-no-unknown */
.phone-input-container ::v-deep(.phone-prefix .v-field) {
  border-end-end-radius: 0 !important;
  border-inline-end: none !important;
  border-start-end-radius: 0 !important;
}

.phone-input-container ::v-deep(.phone-number .v-field) {
  border-end-start-radius: 0 !important;
  border-start-start-radius: 0 !important;
  margin-inline-start: -1px;
}

.phone-input-container ::v-deep(.phone-number .v-field:focus-within) {
  position: relative;
  z-index: 1;
}

.phone-input-container ::v-deep(.phone-prefix .v-field:focus-within) {
  position: relative;
  z-index: 1;
  border-inline-end: 1px solid rgb(var(--v-theme-primary)) !important;
}
/* stylelint-enable selector-pseudo-element-no-unknown */
</style>


